// Sonar presentation for the fast client (ui-map-interface.md §5).
//
// It is `render/sonar.ts` with the PixiJS `Graphics` and the pooled ring
// sprites taken out. Detection stays server-authoritative — the server
// never sends fleets your faction cannot hear — so this layer draws only
// the player's coverage picture: static base coverage, a ring over every
// own fleet that listens, the ping sweeps, the blips they light, and the
// dashed ring of the base under the cursor.
//
// Everything is ONE circle batch and so one draw call. The fleet ring is
// the interesting one: the PixiJS layer draws a 252/4 white ring texture
// scaled to the range, and the same picture is a signed-distance ring of
// `range · RING_RADIUS_K` stroked at `range · RING_WIDTH_K` — the two
// constants the layer already exports for a caller with no sprite.
//
// One change of behaviour against the PixiJS layer, and it is a fix (the
// plan's C.2): the blip loop is pings × ALL fleets there, culled by
// nothing. Here the enemy contacts are gathered once a frame, culled to
// `frame.cull`, and the ping loop walks that short list.

import { outside, type DrawCtx, type FrameInfo, type Layer, type ViewRect } from '../layer';
// The whole layer rides presentation.toml [presentation.sonar] and the
// chrome accent (R312, R323). `ACCENT_COLOR` is the chrome's own cyan and
// not the viewer's faction colour: a Rust Fleet commander still sweeps in
// cyan. The sweep ring of an own Sapper (R2657) reads the pack's sweep
// radius and the A4 lift (`SEA_OBJECTS`) and draws in the dashed white ring
// (`MARKER_NEUTRAL`).
import {
  ACCENT_COLOR, HOVER_DASHES, HOVER_DASH_FILL, MARKER_NEUTRAL, PING_EVERY_SEC, PING_LIFE_SEC,
  SEA_OBJECTS, SONAR_BASE_COVERAGE, SONAR_FLEET_RING, SONAR_HOSTILE, SONAR_HOVER_ALPHA,
  SONAR_HOVER_WIDTH, SONAR_PING_COLOR, SONAR_SWEEP,
} from '../../gen/presentation';
import type { BatchSource, CircleSink } from './batches';
import { zoomOf } from './shared';
import { ShipClass } from '../../gen/proto/aquaphobia';

// The four shapes below are the PixiJS layer's own types, imported and
// never copied: both clients read one fleet snapshot, one ring list and one
// hover state. The imports are type-only, so no engine reaches this bundle.
export type { HoverRing } from '../../render/sonar-types';
// The broadcast breath is one pure function of the flag and the clock, so
// both clients breathe on the same wave (R453, R456; `sonar-types.ts` `ringAlpha`).
import { ringAlpha } from '../../render/sonar-types';
import type { HoverRing, PingFront, SonarSource } from '../../render/sonar-types';
import type { FleetInfo } from '../../render/fleet-info';

export { PING_EVERY_SEC, PING_LIFE_SEC } from '../../gen/presentation';

/** The static coverage circle of a base: a wash with a thin rim (§5). */
export const BASE_COVERAGE = SONAR_BASE_COVERAGE;
/** One live sweep, and the blip it lights on a contact it passes. */
export const SWEEP = SONAR_SWEEP;
/** The coverage ring over a fleet that listens. */
export const FLEET_RING = SONAR_FLEET_RING;

/** Half the ring texture, in texture px: the radius plus half the stroke. */
const RING_HALF = FLEET_RING.texR + FLEET_RING.texWidth / 2;
/** The ring as geometry: `range · k` for the radius and for the width. */
export const RING_RADIUS_K = FLEET_RING.texR / RING_HALF;
export const RING_WIDTH_K = FLEET_RING.texWidth / RING_HALF;

/** A contact fainter than this is still arriving and lights no blip. */
const BLIP_MIN_ALPHA = 0.3;

interface Ping { x: number; y: number; age: number; range: number }

/** Shared empty result, so quiet water allocates nothing per frame. */
const NO_FRONTS: readonly PingFront[] = [];
/** Shared empty inputs, so a layer with no feed yet draws nothing. */
const NO_BASES: SonarSource[] = [];
const NO_FLEETS: FleetInfo[] = [];

export class SonarLayer implements Layer {
  readonly name = 'sonar';

  /**
   * Fires when a ping sweep spawns, with the emitting base's position.
   * The wiring points it at the audio director; this layer stays
   * presentation-only and imports nothing from `audio/`. A sweep spawns
   * only while this layer advances, so the sonar switch silences it too.
   */
  onPing: ((x: number, y: number) => void) | null = null;

  /**
   * The base under the cursor, set by the hit test each pointermove and
   * cleared when the pointer leaves the canvas. Presentation only — this
   * layer never decides what is detected (§5).
   */
  hoverRing: HoverRing | null = null;
  /**
   * Does the layer draw the hover ring? The chart furniture's limit line
   * (card 34) draws the same circle for the site under the pointer, so the
   * scene turns this off while the furniture is on (R563). The ring's data
   * stays: the tooltip reads its range either way.
   */
  hoverRingDrawn = true;
  /**
   * The fleet under the pointer, 0 for none, set by the hit test with the
   * hover ring. Its ring draws brighter, as the selected one does (R2210).
   */
  hoverFleetId = 0;
  /**
   * The sweep radius of an own Sapper, world px (v2-the-deep.md §11.6,
   * R2635, R2659): `CatalogRules.sea_objects.sweep_px` × the A4 lift while
   * the viewer holds the node. The scene writes it; 0 draws no ring.
   */
  sweepPx = 0;

  private readonly circles: CircleSink;
  private shown = true;

  private ownFaction = 0;
  /** The selected fleet's id, 0 for none: its ring draws brighter (R2210). */
  private selectedId = 0;
  private bases: readonly SonarSource[] = NO_BASES;
  private fleets: readonly FleetInfo[] = NO_FLEETS;

  private readonly pings: Ping[] = [];
  private pingTimer = 0;

  /**
   * What the static coverage was last drawn from (R110). It used to be a
   * `basesDrawn` latch: the circles went down on the first tick that had
   * any bases and were never touched again, so the ring stopped tracking
   * the thing it draws the moment anything about it changed.
   */
  private drawn: SonarSource[] = [];

  /** The wavefronts `fronts()` reports, reused between frames. */
  private readonly frontPool: PingFront[] = [];
  /** The contacts a sweep may light, gathered and culled once a frame. */
  private readonly blipCandidates: FleetInfo[] = [];

  /** The frame clock the broadcast ring breathes on, in seconds. */
  private timeS = 0;
  /** The camera zoom of the frame: the sweep ring keeps its screen dash. */
  private zoom = 1;

  constructor(src: BatchSource) {
    this.circles = src.newCircles(512);
  }

  // -- inputs ----------------------------------------------------------------

  /**
   * The frame's sonar picture. What a hull hears is `f.sonarPx`, which the
   * server decides per viewer (R112), so the layer takes no range table.
   */
  feed(
    ownFaction: number,
    bases: readonly SonarSource[],
    fleets: readonly FleetInfo[],
    selectedId = 0,
  ): void {
    this.ownFaction = ownFaction;
    this.selectedId = selectedId;
    this.fleets = fleets;
    // Redraw the coverage when it actually changes, not once and never
    // again (R110). The compare is a handful of numbers against a handful
    // of bases, run on a frame that already builds this array.
    if (this.coverageMoved(bases)) this.remember(bases);
    this.bases = this.drawn;
  }

  /** Forget the static coverage (call when a new hello rebuilds the scene). */
  reset(): void {
    this.drawn.length = 0;
    this.bases = NO_BASES;
    this.pings.length = 0;
  }

  // -- the frame -------------------------------------------------------------

  update(frame: FrameInfo): void {
    this.timeS = frame.timeS;
    this.zoom = zoomOf(frame.cam.zoom);
    this.advance(frame.dtMs / 1000);
    this.pack(frame.cull);
  }

  draw(ctx: DrawCtx): void {
    this.circles.flush(ctx.cam);
  }

  setVisible(v: boolean): void { this.shown = v; }
  get visible(): boolean { return this.shown; }

  /**
   * The wavefronts alive this frame, as anything that reacts to them sees
   * them (R101 ping-lit snow). The radius is the same `age/life × range`
   * the sweep is drawn at, so a layer that lights up as the ring passes
   * reads the ring and not a second clock of its own.
   *
   * The array and its objects are reused between frames, because the one
   * consumer reads them inside the frame it asked in.
   */
  fronts(): readonly PingFront[] {
    if (this.pings.length === 0) return NO_FRONTS;
    this.frontPool.length = this.pings.length;
    for (let i = 0; i < this.pings.length; i++) {
      const p = this.pings[i];
      const t = p.age / PING_LIFE_SEC;
      const slot = this.frontPool[i];
      if (slot) {
        slot.x = p.x; slot.y = p.y; slot.r = t * p.range; slot.strength = 1 - t;
      } else {
        this.frontPool[i] = { x: p.x, y: p.y, r: t * p.range, strength: 1 - t };
      }
    }
    return this.frontPool;
  }

  // -- the coverage compare (R110) -------------------------------------------

  /** Has anything about the static coverage changed since it was drawn? */
  coverageMoved(bases: readonly SonarSource[]): boolean {
    if (bases.length !== this.drawn.length) return true;
    for (let i = 0; i < bases.length; i++) {
      const a = bases[i], b = this.drawn[i];
      if (a.x !== b.x || a.y !== b.y || a.range !== b.range) return true;
    }
    return false;
  }

  /** Copy into the kept array, reusing its objects so a steady state is free. */
  private remember(bases: readonly SonarSource[]): void {
    this.drawn.length = bases.length;
    for (let i = 0; i < bases.length; i++) {
      const b = bases[i];
      const slot = this.drawn[i];
      if (slot) { slot.x = b.x; slot.y = b.y; slot.range = b.range; }
      else this.drawn[i] = { x: b.x, y: b.y, range: b.range };
    }
  }

  // -- the sweeps ------------------------------------------------------------

  private advance(dt: number): void {
    this.pingTimer += dt;
    if (this.pingTimer > PING_EVERY_SEC && this.bases.length > 0) {
      this.pingTimer = 0;
      const b = this.bases[Math.floor(Math.random() * this.bases.length)];
      this.pings.push({ x: b.x, y: b.y, age: 0, range: b.range });
      this.onPing?.(b.x, b.y);
    }
    // The live sweeps close up in place, in their own order, so a death
    // allocates nothing (§10.4).
    const pings = this.pings;
    let live = 0;
    for (let i = 0; i < pings.length; i++) {
      const p = pings[i];
      p.age += dt;
      if (p.age <= PING_LIFE_SEC) pings[live++] = p;
    }
    pings.length = live;
  }

  // -- packing ---------------------------------------------------------------

  private pack(cull: ViewRect): void {
    const c = this.circles;
    c.begin();

    // Static coverage: the wash of every base this viewer receives, then
    // the thin rim over it, in the PixiJS layer's two passes.
    for (const b of this.bases) c.disc(b.x, b.y, b.range, ACCENT_COLOR, BASE_COVERAGE.fillAlpha);
    for (const b of this.bases) {
      c.ring(b.x, b.y, b.range, BASE_COVERAGE.strokeWidth, ACCENT_COLOR, BASE_COVERAGE.strokeAlpha);
    }

    // Coverage rings: one per fleet that is a source **for this viewer**
    // (R112). The server marks that per fleet, so a squadmate's hull
    // carries a range only once the squad has paid both sides of the
    // Comms fleet gate, and a stranger's never does.
    // An Oracle that lends its array to the faction breathes instead of
    // holding one alpha (R453, R456). The tint stays the accent: the pulse is
    // the whole signal (§5), and `ringAlpha` is the one wave both clients
    // read. The selected fleet's ring and the hovered fleet's ring take the
    // brighter alpha (R2210).
    for (const f of this.fleets) {
      const range = f.sonarPx;
      if (range <= 0) continue;
      const lit = f.id === this.selectedId || f.id === this.hoverFleetId;
      c.ring(f.x, f.y, range * RING_RADIUS_K, range * RING_WIDTH_K,
        ACCENT_COLOR, ringAlpha(f.sharing, this.timeS, lit));
    }

    this.packSapperSweeps(c, cull);
    this.packSweeps(c, cull);
    this.packHoverRing(c);
  }

  /**
   * The sweep ring of every own Sapper (v2-the-deep.md §15.6, R2657): the
   * dashed white ring of the `guide` switch of round 071, at the sweep
   * radius × the A4 lift. A Sapper shows its commander every sea mine
   * inside it (§11.1), so the ring is own information, and a contact or a
   * squad mate's Sapper draws none. The dash keeps its screen length at
   * every zoom. A world with `sapper` off has no Sapper, and the ring
   * draws nothing.
   */
  private packSapperSweeps(c: CircleSink, cull: ViewRect): void {
    const r = this.sweepPx, z = this.zoom;
    if (!(r > 0)) return;
    for (const f of this.fleets) {
      if (!f.own || f.cls !== ShipClass.SHIP_CLASS_SAPPER) continue;
      if (f.x + r < cull.x0 || f.x - r > cull.x1 || f.y + r < cull.y0 || f.y - r > cull.y1) continue;
      const count = Math.max(8, Math.min(720, Math.round((Math.PI * r * z) / SEA_OBJECTS.sweepDashPx)));
      c.dashed(f.x, f.y, r, SEA_OBJECTS.sweepWidthPx / z, count, 0.5, MARKER_NEUTRAL, SEA_OBJECTS.sweepAlpha);
    }
  }

  private packSweeps(c: CircleSink, cull: ViewRect): void {
    if (this.pings.length === 0) return;
    // The contacts a sweep may light, gathered once and culled once. The
    // PixiJS layer walks every fleet inside every ping's loop and culls
    // nothing.
    const cand = this.blipCandidates;
    cand.length = 0;
    for (const f of this.fleets) {
      if (f.faction === this.ownFaction || f.alpha < BLIP_MIN_ALPHA) continue;
      if (outside(f.x, f.y, cull.x0, cull.y0, cull.x1, cull.y1)) continue;
      cand.push(f);
    }

    for (const p of this.pings) {
      const t = p.age / PING_LIFE_SEC;
      const r = t * p.range;
      c.ring(p.x, p.y, r, SWEEP.width * (1 - t), SONAR_PING_COLOR, SWEEP.alpha * (1 - t));
      for (const f of cand) {
        const d = Math.hypot(f.x - p.x, f.y - p.y);
        if (Math.abs(d - r) < SWEEP.blipWindow) {
          c.disc(f.x, f.y, SWEEP.blipR, SONAR_PING_COLOR, SWEEP.blipAlpha * (1 - t));
        }
      }
    }
  }

  /**
   * The hovered base's listening circle: the dash and the stroke of
   * `[presentation.sonar]` (`hover_*`), cyan for own and red for hostile
   * (§5, built in R106).
   *
   * The PixiJS layer opens every dash with its own `moveTo`, because an
   * `arc()` from wherever the pen is joins the dashes into a solid ring.
   * A dashed circle instance has no pen: the fragment shader cuts the
   * dashes out of one ring, and the count and the fill are the spec's.
   */
  private packHoverRing(c: CircleSink): void {
    const h = this.hoverRing;
    if (!this.hoverRingDrawn || !h || h.range <= 0) return;
    c.dashed(
      h.x, h.y, h.range, SONAR_HOVER_WIDTH, HOVER_DASHES, HOVER_DASH_FILL,
      h.own ? ACCENT_COLOR : SONAR_HOSTILE, SONAR_HOVER_ALPHA,
    );
  }
}
